/**
 * Exponential growth: doubling time, specific growth rate, generations.
 *
 * N = N₀ × 2^(t ÷ Td), so  Td = t × ln 2 ÷ ln(N ÷ N₀)  (the logarithm can be in any base
 * — Roth 2006, "Doubling Time Computing"). The specific growth rate is μ = ln(N ÷ N₀) ÷ t
 * and the number of generations is n = log₂(N ÷ N₀). All of it holds only while cells
 * grow exponentially: lag, plateau and death phases are not described by it.
 */

export type TimeUnit = "min" | "h" | "d";

export const HOURS_PER_UNIT: Record<TimeUnit, number> = {
  min: 1 / 60,
  h: 1,
  d: 24,
};

export function unitsToHours(value: number, unit: TimeUnit): number {
  return value * HOURS_PER_UNIT[unit];
}

export function hoursToUnits(hours: number, unit: TimeUnit): number {
  return hours / HOURS_PER_UNIT[unit];
}

export type GrowthError = "initial" | "final" | "time" | "doubling" | "target";

export interface DoublingResult {
  /** Doubling time in the unit the elapsed time was entered in. */
  doublingTime: number;
  /** Specific growth rate μ, per time unit. */
  rate: number;
  generations: number;
  /** Increase per time unit, as a percentage: (e^μ − 1) × 100. */
  percentPerUnit: number;
  foldChange: number;
}

export type DoublingOutcome =
  | { kind: "growth"; result: DoublingResult }
  | { kind: "none" }
  | { kind: "decline"; halfLife: number; generations: number }
  | { kind: "error"; error: GrowthError };

export function doublingFromCounts(
  initial: number,
  final: number,
  time: number
): DoublingOutcome {
  if (!(initial > 0)) {
    return { kind: "error", error: "initial" };
  }
  if (!(final > 0)) {
    return { kind: "error", error: "final" };
  }
  if (!(time > 0)) {
    return { kind: "error", error: "time" };
  }
  const logRatio = Math.log(final / initial);
  if (logRatio === 0) {
    return { kind: "none" };
  }
  if (logRatio < 0) {
    return {
      kind: "decline",
      halfLife: (time * Math.LN2) / -logRatio,
      generations: logRatio / Math.LN2,
    };
  }
  const rate = logRatio / time;
  return {
    kind: "growth",
    result: {
      doublingTime: (time * Math.LN2) / logRatio,
      rate,
      generations: logRatio / Math.LN2,
      percentPerUnit: (Math.exp(rate) - 1) * 100,
      foldChange: final / initial,
    },
  };
}

/** Count after `time` when the population doubles every `doublingTime`. */
export function countAfter(
  initial: number,
  doublingTime: number,
  time: number
): { value: number; generations: number } | { error: GrowthError } {
  if (!(initial > 0)) {
    return { error: "initial" };
  }
  if (!(doublingTime > 0)) {
    return { error: "doubling" };
  }
  if (!(time >= 0)) {
    return { error: "time" };
  }
  const generations = time / doublingTime;
  return { value: initial * 2 ** generations, generations };
}

/** Time to grow from `initial` to `target` at a given doubling time. */
export function timeToReach(
  initial: number,
  target: number,
  doublingTime: number
): { time: number; generations: number } | { error: GrowthError } {
  if (!(initial > 0)) {
    return { error: "initial" };
  }
  if (!(target > 0)) {
    return { error: "target" };
  }
  if (!(doublingTime > 0)) {
    return { error: "doubling" };
  }
  const generations = Math.log2(target / initial);
  return { time: generations * doublingTime, generations };
}
